import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from "react";
import {
  alpha,
  Box,
  Button,
  Chip,
  CircularProgress,
  Drawer,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme
} from "@mui/material";
import ArticleIcon from "@mui/icons-material/Article";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CloseIcon from "@mui/icons-material/Close";
import PolicyIcon from "@mui/icons-material/Policy";
import { AuthContext } from "../../contexts/AuthContext";
import { useErrorDialog } from "../../contexts/ErrorDialogContext";

const FILTER_ALL = "__all__";
const FILTER_UNTAGGED = "__untagged__";

/** Matches DisclosureCenterPage list column */
const LIST_COLUMN_WIDTH_MD = "clamp(300px, 30vw, 400px)";

function extractList(res) {
  if (!res) return [];
  if (Array.isArray(res)) return res;
  if (Array.isArray(res.data)) return res.data;
  return [];
}

function normalizePreview(text) {
  const raw = String(text || "")
    .replace(/\s+/g, " ")
    .trim();
  if (raw.length <= 20) return raw;
  return `${raw.slice(0, 20)}…`;
}

/** Subsequence fuzzy match (lightweight “模糊”) */
function fuzzyMatch(haystack, query) {
  const h = String(haystack || "").toLowerCase();
  const q = String(query || "")
    .trim()
    .toLowerCase();
  if (!q) return true;
  let qi = 0;
  for (let i = 0; i < h.length && qi < q.length; i += 1) {
    if (h[i] === q[qi]) qi += 1;
  }
  return qi === q.length;
}

function matchesSearch(row, q) {
  if (!q.trim()) return true;
  const title = row.title || "";
  const body = row.statement_text || "";
  return fuzzyMatch(title, q) || fuzzyMatch(body, q);
}

function disclosurePlaceholder(d_id) {
  return `{{DISC_${d_id}}}`;
}

const GlobalDisclosureLibrary = ({ company, open, onOpenChange }) => {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [tags, setTags] = useState([]);
  const [disclosures, setDisclosures] = useState([]);
  const [selectedFilter, setSelectedFilter] = useState(FILTER_ALL);
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [hoverRowId, setHoverRowId] = useState(null);
  const [toast, setToast] = useState(null);

  const authContext = useContext(AuthContext);
  const { listDisclosures, listDisclosureTags } = authContext;
  const handleError = useErrorDialog();

  const topZ = theme.zIndex.modal + 100;

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [dRes, tRes] = await Promise.all([
        listDisclosures({ c_id: company?.c_id, is_active: true }),
        listDisclosureTags()
      ]);
      setDisclosures(extractList(dRes));
      setTags(
        extractList(tRes).sort((a, b) =>
          String(a.name || "").localeCompare(String(b.name || ""), undefined, {
            sensitivity: "base"
          })
        )
      );
    } catch (err) {
      handleError(err, err?.message || "Failed to load disclosure library");
      setDisclosures([]);
      setTags([]);
    } finally {
      setLoading(false);
    }
  }, [listDisclosures, listDisclosureTags]);

  useEffect(() => {
    if (open) loadData();
  }, [open, loadData]);

  const hasUntagged = useMemo(
    () => disclosures.some((d) => !d.disclosure_tags?.length),
    [disclosures]
  );

  const filteredByCategory = useMemo(() => {
    if (selectedFilter === FILTER_ALL) return disclosures;
    if (selectedFilter === FILTER_UNTAGGED) {
      return disclosures.filter((d) => !d.disclosure_tags?.length);
    }
    return disclosures.filter((d) =>
      d.disclosure_tags?.some((t) => String(t.t_id) === String(selectedFilter))
    );
  }, [disclosures, selectedFilter]);

  const filteredList = useMemo(() => {
    return filteredByCategory.filter((row) => matchesSearch(row, search));
  }, [filteredByCategory, search]);

  const selectedRow = useMemo(
    () => filteredList.find((d) => d.d_id === selectedId) || null,
    [filteredList, selectedId]
  );

  useEffect(() => {
    if (selectedId && !filteredList.some((d) => d.d_id === selectedId)) {
      setSelectedId(null);
    }
  }, [filteredList, selectedId]);

  useEffect(() => {
    const onKey = (e) => {
      const el = e.target;
      const tag = el?.tagName;
      const editable =
        tag === "INPUT" ||
        tag === "TEXTAREA" ||
        tag === "SELECT" ||
        el?.isContentEditable;

      const isSlashCombo =
        (e.ctrlKey || e.metaKey) &&
        !e.shiftKey &&
        (e.key === "/" || e.code === "Slash");

      const isShiftL =
        (e.ctrlKey || e.metaKey) &&
        e.shiftKey &&
        (e.key === "l" || e.key === "L");

      if (!isSlashCombo && !isShiftL) return;
      if (editable) return;

      e.preventDefault();
      onOpenChange((prev) => !prev);
    };

    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [onOpenChange]);

  const copyText = async (text, message = "Copied!") => {
    try {
      await navigator.clipboard.writeText(String(text ?? ""));
      setToast(message);
    } catch {
      setToast("Copy failed");
    }
  };

  const handleCopyFull = () => {
    if (!selectedRow) return;
    copyText(selectedRow.statement_text, "Copied!");
  };

  const handleCopyTitle = () => {
    if (!selectedRow) return;
    copyText(selectedRow.title, "Copied!");
  };

  const drawer = (
    <Drawer
      anchor='right'
      open={open}
      onClose={() => onOpenChange(false)}
      sx={{ zIndex: topZ }}
      ModalProps={{ keepMounted: false }}
      PaperProps={{
        "data-disclosure-library": true,
        sx: {
          width: { xs: "100%", sm: "min(96vw, 1100px)" },
          maxWidth: "100vw",
          display: "flex",
          flexDirection: "column",
          bgcolor: "background.default"
        }
      }}
    >
      <Box
        data-disclosure-library
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          minHeight: 0,
          bgcolor: "background.default"
        }}
      >
        <Box
          sx={{
            px: { xs: 2, md: 3 },
            py: { xs: 1.5, md: 2 },
            borderBottom: "1px solid",
            borderColor: "divider",
            bgcolor: "background.paper",
            flexShrink: 0
          }}
        >
          <Stack
            direction='row'
            alignItems='center'
            justifyContent='space-between'
            spacing={2}
          >
            <Stack direction='row' alignItems='center' spacing={1.5}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "12px",
                  background:
                    "linear-gradient(135deg, #603F97 0%, #D31D83 100%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0
                }}
              >
                <PolicyIcon sx={{ color: "white", fontSize: 22 }} />
              </Box>
              <Box>
                <Typography variant='h6' fontWeight={800}>
                  Disclosure library
                </Typography>
                <Typography variant='caption' color='text.secondary'>
                  Browse and copy standard disclosures for your workflow.
                </Typography>
              </Box>
            </Stack>
            <IconButton
              aria-label='Close library'
              onClick={() => onOpenChange(false)}
              edge='end'
              sx={{ flexShrink: 0 }}
            >
              <CloseIcon />
            </IconButton>
          </Stack>
        </Box>

        {loading ? (
          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              py: 6,
              bgcolor: "background.default"
            }}
          >
            <CircularProgress size={32} />
          </Box>
        ) : (
          <Box
            sx={{
              flex: 1,
              display: "flex",
              minHeight: 0,
              flexDirection: { xs: "column", md: "row" },
              overflow: "hidden"
            }}
          >
            {/* A — Filter */}
            <Box
              component='aside'
              sx={{
                flex: { xs: 1, md: "0 0 auto" },
                width: { xs: "100%", md: 220 },
                maxWidth: { xs: "100%", md: 260 },
                maxHeight: { xs: 200, md: "none" },
                borderBottom: { xs: "1px solid", md: "none" },
                borderRight: { md: "1px solid" },
                borderColor: "divider",
                bgcolor: "#FFFFFF",
                display: "flex",
                flexDirection: "column",
                minHeight: 0
              }}
            >
              <Box
                sx={{
                  flex: 1,
                  overflow: "auto",
                  p: { xs: 1.5, md: 2 },
                  bgcolor: "#FAFBFC"
                }}
              >
                <Typography
                  variant='overline'
                  color='primary'
                  fontWeight={700}
                  display='block'
                  sx={{
                    mb: 1.5,
                    letterSpacing: "0.5px",
                    textTransform: "uppercase"
                  }}
                >
                  Category (tags)
                </Typography>
                <List dense sx={{ py: 0, px: 0 }}>
                  <ListItemButton
                    selected={selectedFilter === FILTER_ALL}
                    onClick={() => {
                      setSelectedFilter(FILTER_ALL);
                    }}
                    sx={{
                      borderRadius: 2,
                      mb: 0.5,
                      "&.Mui-selected": {
                        bgcolor: alpha(theme.palette.primary.main, 0.08)
                      }
                    }}
                  >
                    <ListItemText primary='All' />
                  </ListItemButton>
                  {hasUntagged ? (
                    <ListItemButton
                      selected={selectedFilter === FILTER_UNTAGGED}
                      onClick={() => setSelectedFilter(FILTER_UNTAGGED)}
                      sx={{
                        borderRadius: 2,
                        mb: 0.5,
                        "&.Mui-selected": {
                          bgcolor: alpha(theme.palette.primary.main, 0.08)
                        }
                      }}
                    >
                      <ListItemText primary='Untagged' />
                    </ListItemButton>
                  ) : null}
                  {tags.map((t) => (
                    <ListItemButton
                      key={t.t_id}
                      selected={selectedFilter === String(t.t_id)}
                      onClick={() => setSelectedFilter(String(t.t_id))}
                      sx={{
                        borderRadius: 2,
                        mb: 0.5,
                        "&.Mui-selected": {
                          bgcolor: alpha(theme.palette.primary.main, 0.08)
                        }
                      }}
                    >
                      <ListItemText primary={t.name || "(unnamed)"} />
                    </ListItemButton>
                  ))}
                </List>
              </Box>
            </Box>

            {/* B — Statements */}
            <Box
              component='aside'
              sx={{
                flex: { xs: "none", md: "0 0 auto" },
                width: { xs: "100%", md: LIST_COLUMN_WIDTH_MD },
                maxWidth: { xs: "100%", md: 400 },
                maxHeight: { xs: 280, md: "none" },
                borderBottom: { xs: "1px solid", md: "none" },
                borderRight: { md: "1px solid" },
                borderColor: "divider",
                bgcolor: "#FFFFFF",
                display: "flex",
                flexDirection: "column",
                minHeight: 0
              }}
            >
              <Box
                sx={{
                  flex: 1,
                  overflow: "hidden",
                  display: "flex",
                  flexDirection: "column",
                  minHeight: 0,
                  p: { xs: 1.5, md: 2 },
                  bgcolor: "#FAFBFC"
                }}
              >
                <Typography
                  variant='overline'
                  color='primary'
                  fontWeight={700}
                  display='block'
                  sx={{
                    mb: 1.5,
                    letterSpacing: "0.5px",
                    textTransform: "uppercase",
                    flexShrink: 0
                  }}
                >
                  Statements
                </Typography>
                <TextField
                  size='small'
                  placeholder='Search statements…'
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  fullWidth
                  sx={{
                    mb: 1.5,
                    flexShrink: 0,
                    "& .MuiOutlinedInput-root": {
                      height: 40,
                      borderRadius: 2,
                      bgcolor: "background.paper",
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "grey.300"
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "primary.main",
                        boxShadow: "0 0 0 3px rgba(96, 63, 151, 0.1)"
                      }
                    },
                    "& .MuiInputAdornment-positionStart": {
                      ml: 0.5
                    }
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position='start'>
                        <PolicyIcon
                          sx={{
                            fontSize: 20,
                            color: "text.secondary"
                          }}
                        />
                      </InputAdornment>
                    )
                  }}
                />
                <Stack
                  spacing={1}
                  sx={{
                    flex: 1,
                    overflowY: "auto",
                    minHeight: 0,
                    p: 1,
                    "&::-webkit-scrollbar": { width: 6 },
                    "&::-webkit-scrollbar-track": { background: "transparent" },
                    "&::-webkit-scrollbar-thumb": {
                      bgcolor: "grey.200",
                      borderRadius: 3
                    }
                  }}
                >
                  {filteredList.length === 0 ? (
                    <Typography
                      variant='body2'
                      color='text.secondary'
                      sx={{ py: 2, textAlign: "center" }}
                    >
                      No statements match.
                    </Typography>
                  ) : (
                    filteredList.map((row) => {
                      const isSel = selectedId === row.d_id;
                      const isHover = hoverRowId === row.d_id;
                      return (
                        <Box
                          key={row.d_id}
                          onClick={() => setSelectedId(row.d_id)}
                          onMouseEnter={() => setHoverRowId(row.d_id)}
                          onMouseLeave={() => setHoverRowId(null)}
                          sx={{
                            p: 2,
                            borderRadius: 2.5,
                            border: "1px solid",
                            borderColor: "divider",
                            bgcolor: isSel
                              ? alpha(theme.palette.primary.main, 0.04)
                              : "background.paper",
                            cursor: "pointer",
                            transition: "all 0.2s ease-in-out",
                            boxShadow: isSel
                              ? `0 0 0 1px ${theme.palette.primary.main}`
                              : "none",
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 1,
                            "&:hover": {
                              bgcolor: isSel
                                ? alpha(theme.palette.primary.main, 0.06)
                                : alpha(theme.palette.grey[500], 0.04),
                              borderColor: alpha(
                                theme.palette.primary.main,
                                0.3
                              ),
                              boxShadow: isSel
                                ? `0 0 0 1px ${theme.palette.primary.main}`
                                : `0 4px 12px ${alpha(
                                    theme.palette.common.black,
                                    0.05
                                  )}`,
                              transform: "translateY(-1px)"
                            }
                          }}
                        >
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                              variant='subtitle2'
                              fontWeight={600}
                              sx={{
                                color: isSel ? "primary.main" : "text.primary",
                                lineHeight: 1.5,
                                display: "-webkit-box",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                                mb: 0.5
                              }}
                            >
                              {row.title || "(No title)"}
                            </Typography>
                            <Typography
                              variant='caption'
                              color='text.secondary'
                              sx={{
                                display: "-webkit-box",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden"
                              }}
                            >
                              {normalizePreview(row.statement_text)}
                            </Typography>
                          </Box>
                          <Tooltip title='Copy statement'>
                            <span>
                              <IconButton
                                size='small'
                                tabIndex={-1}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  copyText(row.statement_text, "Copied!");
                                }}
                                sx={{
                                  mt: -0.25,
                                  opacity: isHover || isSel ? 1 : 0,
                                  transition: "opacity 0.15s ease"
                                }}
                              >
                                <ContentCopyIcon fontSize='small' />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                      );
                    })
                  )}
                </Stack>
              </Box>
            </Box>

            {/* C — Detail (DisclosureInspector-style) */}
            <Box
              component='main'
              sx={{
                flex: 1,
                minWidth: { md: "min(480px, 45vw)" },
                display: "flex",
                flexDirection: "column",
                minHeight: { xs: 240, md: 0 },
                overflow: "hidden",
                bgcolor: "#FAFAFA",
                borderLeft: { md: "1px solid" },
                borderColor: { md: "white" },
                boxShadow: (t) =>
                  `-12px 0 40px ${alpha(t.palette.common.black, 0.06)}`
              }}
            >
              {!selectedRow ? (
                <Stack
                  direction='column'
                  alignItems='center'
                  justifyContent='center'
                  spacing={2}
                  p={4}
                  textAlign='center'
                  flex={1}
                >
                  <Box
                    sx={{
                      width: 64,
                      height: 64,
                      borderRadius: "50%",
                      bgcolor: "grey.100",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mb: 2,
                      color: "grey.400"
                    }}
                  >
                    <ArticleIcon sx={{ fontSize: 32 }} />
                  </Box>
                  <Box>
                    <Typography
                      variant='h6'
                      color='text.primary'
                      fontWeight={600}
                      sx={{ mb: 0.5 }}
                    >
                      No statement selected
                    </Typography>
                    <Typography
                      variant='body2'
                      color='text.secondary'
                      sx={{ maxWidth: 320, mx: "auto" }}
                    >
                      Select an item from the list to view its full text and
                      copy options.
                    </Typography>
                  </Box>
                </Stack>
              ) : (
                <>
                  <Box
                    sx={{
                      px: { xs: 2.5, md: 4 },
                      py: 2.5,
                      borderBottom: "1px solid",
                      borderColor: "divider",
                      bgcolor: "background.paper",
                      boxShadow: "0 2px 10px rgba(0,0,0,0.02)",
                      zIndex: 1,
                      flexShrink: 0
                    }}
                  >
                    <Typography
                      variant='h5'
                      fontWeight={800}
                      color='text.primary'
                      sx={{
                        lineHeight: 1.2,
                        wordBreak: "break-word",
                        letterSpacing: "-0.015em"
                      }}
                    >
                      {selectedRow.title || "(No title)"}
                    </Typography>
                    {selectedRow.disclosure_tags?.length ? (
                      <Stack
                        direction='row'
                        flexWrap='wrap'
                        useFlexGap
                        spacing={0.75}
                        sx={{ gap: 0.75, mt: 1.5 }}
                      >
                        {selectedRow.disclosure_tags.map((t) => (
                          <Chip
                            key={t.t_id}
                            label={t.name}
                            size='small'
                            variant='outlined'
                            color='primary'
                            sx={{
                              bgcolor: theme.palette.primary.main,
                              color: theme.palette.common.white,
                              fontWeight: 600,
                              borderColor: theme.palette.primary.main
                            }}
                          />
                        ))}
                      </Stack>
                    ) : null}
                  </Box>

                  <Box
                    sx={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      minHeight: 0,
                      overflow: "hidden",
                      bgcolor: "#FFFFFF"
                    }}
                  >
                    <Box
                      sx={{
                        px: { xs: 2.5, md: 4 },
                        py: 3,
                        flex: 1,
                        minHeight: 0,
                        overflow: "auto",
                        borderBottom: "1px solid",
                        borderColor: "divider",
                        "&::-webkit-scrollbar": { width: 6 },
                        "&::-webkit-scrollbar-track": {
                          background: "transparent"
                        },
                        "&::-webkit-scrollbar-thumb": {
                          bgcolor: "grey.200",
                          borderRadius: 3
                        }
                      }}
                    >
                      <Typography
                        variant='overline'
                        color='primary'
                        fontWeight={700}
                        display='block'
                        sx={{
                          mb: 1.5,
                          letterSpacing: "0.5px",
                          textTransform: "uppercase"
                        }}
                      >
                        Statement content
                      </Typography>
                      <Typography
                        variant='body1'
                        color='text.primary'
                        sx={{
                          whiteSpace: "pre-wrap",
                          wordBreak: "break-word",
                          lineHeight: 1.8,
                          fontSize: "0.95rem",
                          color: "grey.700"
                        }}
                      >
                        {selectedRow.statement_text || (
                          <Typography
                            variant='body2'
                            color='text.disabled'
                            sx={{ fontStyle: "italic" }}
                          >
                            No statement text available.
                          </Typography>
                        )}
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        p: 3,
                        borderTop: "1px solid",
                        borderColor: "divider",
                        bgcolor: alpha(theme.palette.grey[500], 0.06),
                        flexShrink: 0
                      }}
                    >
                      <Typography
                        variant='subtitle2'
                        fontWeight={800}
                        sx={{ mb: 1.5 }}
                      >
                        Actions
                      </Typography>
                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={1}
                      >
                        <Button
                          variant='outlined'
                          size='small'
                          startIcon={<ContentCopyIcon fontSize='small' />}
                          onClick={handleCopyTitle}
                          sx={{
                            borderRadius: 1.5,
                            textTransform: "none",
                            fontWeight: 600,
                            px: 2,
                            borderColor: "divider",
                            color: "text.secondary",
                            "&:hover": {
                              borderColor: "primary.main",
                              color: "primary.main",
                              backgroundColor: alpha(
                                theme.palette.primary.main,
                                0.04
                              )
                            }
                          }}
                        >
                          Copy title
                        </Button>
                        <Button
                          variant='contained'
                          size='small'
                          startIcon={<ContentCopyIcon fontSize='small' />}
                          onClick={handleCopyFull}
                          sx={{
                            borderRadius: 1.5,
                            textTransform: "none",
                            fontWeight: 700,
                            px: 2
                          }}
                        >
                          Copy statement
                        </Button>
                      </Stack>
                    </Box>
                  </Box>
                </>
              )}
            </Box>
          </Box>
        )}
      </Box>
    </Drawer>
  );

  return (
    <>
      {drawer}

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={2000}
        onClose={() => setToast(null)}
        message={toast}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        sx={{ zIndex: topZ + 2 }}
      />
    </>
  );
};

export default GlobalDisclosureLibrary;
